import type {ActiveRingInvite, CallIntent, RoomCallSummary} from '@phonehq/rtc-core';

export interface ActiveCallRow {
    readonly roomId: string;
    readonly summary?: RoomCallSummary;
    readonly invite?: ActiveRingInvite;
    readonly intent: CallIntent;
    readonly full: boolean;
    readonly inThisCall: boolean;
}

export interface ActiveCallsInput {
    readonly calls: ReadonlyMap<string, RoomCallSummary>;
    readonly invites: readonly ActiveRingInvite[];
    readonly maxParticipants: number;
    readonly isListed: (roomId: string) => boolean;
    readonly peopleIn: (summary: RoomCallSummary) => number;
}

const startedAtOf = (row: ActiveCallRow): number =>
    row.summary?.startedAt ?? (row.invite ? Date.parse(row.invite.createdAt) || 0 : 0);

export const activeCallRows = ({calls, invites, maxParticipants, isListed, peopleIn}: ActiveCallsInput): ActiveCallRow[] => {
    const inviteByRoom = new Map(invites.map(invite => [invite.roomId, invite]));
    const roomIds = new Set([...calls.keys(), ...inviteByRoom.keys()]);
    const rows: ActiveCallRow[] = [];
    roomIds.forEach(roomId => {
        const summary = calls.get(roomId);
        const invite = inviteByRoom.get(roomId);
        if (summary && summary.participantCount === 0) {
            return;
        }
        if (!invite && !isListed(roomId)) {
            return;
        }
        const inThisCall = !!summary?.includesMe && !summary.onOtherDevice;
        rows.push({
            roomId,
            summary,
            invite: inThisCall ? undefined : invite,
            intent: summary?.intent ?? invite?.intent ?? 'audio',
            full: !!summary && !summary.includesMe && peopleIn(summary) >= maxParticipants,
            inThisCall,
        });
    });
    return rows.sort((left, right) => {
        if (!!left.invite !== !!right.invite) {
            return left.invite ? -1 : 1;
        }
        return startedAtOf(right) - startedAtOf(left);
    });
};
